import React, { useState } from "react";
import {
  FormatUTCDate,
  FormatUTCTime,
} from "../../../../../../utilities/DateFormater";
import ChevronLeft from "./ChevronLeft";
import { DialogControl } from "../../../../../../components/controls/DialogControl";
import ReactMarkdown from "react-markdown";
import { Tooltip } from "@mui/material";
import { ThumbUpSharp } from "@mui/icons-material";
import ThumbsUp from "../../ThumbsUp";

const truncate = (value, maxLen = 120) => {
  if (!value) return value;
  if (value.length > maxLen) {
    return value.substring(0, maxLen) + "...";
  }
  return value;
};

export default function AccordionTable({ headers, data, renderRowContent }) {
  const rowKey = "SessionId";
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [openSystemPromptModal, setOpenSystemPromptModal] = useState(false);
  const [selectedSystemPrompt, setSelectedSystemPrompt] = useState(null);

  const handleExpanded = (rowData) => {
    let open = false;
    const nextExpandedRowKeys = [...expandedRowKeys];
    const index = nextExpandedRowKeys.indexOf(rowData[rowKey]);

    if (index > -1) {
      // Row is already expanded, collapse it
      nextExpandedRowKeys.splice(index, 1);
    } else {
      // Row is collapsed, expand it
      nextExpandedRowKeys.push(rowData[rowKey]);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
  };

  const handleExpandedSubRow = (chatId) => {
    const nextExpandedRows = [...expandedRows];
    const index = nextExpandedRows.indexOf(chatId);

    if (index > -1) {
      // Row is already expanded, collapse it
      nextExpandedRows.splice(index, 1);
    } else {
      // Row is collapsed, expand it
      nextExpandedRows.push(chatId);
    }

    setExpandedRows(nextExpandedRows);
  };

  const renderRowExpanded = (rowData) => {
    const handleOpenSystemPromptModal = (chat) => {
      setOpenSystemPromptModal(true);
      setSelectedSystemPrompt(chat?.SystemPrompt);
    };

    console.log(rowData);

    return (
      <div className="chat-row-expanded">
        {rowData?.Chats?.map((chat, index) => (
          <>
            <DialogControl
              openDialog={openSystemPromptModal}
              onCancel={() => setOpenSystemPromptModal(false)}
              cancelText={"Close"}
            >
              <ReactMarkdown>
                {selectedSystemPrompt || "*No content provided*"}
              </ReactMarkdown>
            </DialogControl>
            <div key={chat.ChatId} className="chat-row-prompt">
              <div
                className="chat-prompt-row"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandedSubRow(chat.ChatId);
                }}
              >
                <div>
                  <div>{FormatUTCDate(chat.Date)}</div>
                  <div>{FormatUTCTime(chat.Date)}</div>
                </div>
                <div className="chat-column chat-column-gap">
                  <div>
                    <div className="chat-label">Prompt</div>
                    <div className="chat-value">{chat.Prompt}</div>
                  </div>
                  <div>
                    <div className="chat-label">Response</div>
                    <div className="chat-value">
                      {chat.Response || "No response"}
                    </div>
                  </div>
                  <div className="chat-row">
                    <div className="chat-row chat-box-style">
                      <div className="chat-row chat-box-box chat-box-style">
                        <div>
                          <div className="chat-box-title-style">
                            Session Id:
                          </div>
                          <div className="chat-box-title-style">Chat Id:</div>
                        </div>
                        <div>
                          <div>{rowData.SessionId}</div>
                          <div>{chat.ChatId}</div>
                        </div>
                      </div>
                      <div className="chat-column chat-box-box">
                        <div className="chat-box-title-style">
                          System Prompt
                        </div>
                        <div
                          className="chat-box-underline-style"
                          onClick={() => handleOpenSystemPromptModal(chat)}
                        >
                          Review
                        </div>
                      </div>
                      {chat?.Citations?.length > 0 && (
                        <div className="chat-column chat-box-box">
                          <div className="chat-box-title-style">References</div>
                          <div className="chat-row chat-box-style">
                            {chat?.Citations?.map((reference, index) => (
                              <Tooltip
                                title={reference.document_name}
                                placement="top-start"
                                className="chat-box-underline-style"
                                key={index}
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      {
                                        name: "offset",
                                        options: {
                                          offset: [-5, -5],
                                        },
                                      },
                                    ],
                                  },
                                }}
                                arrow
                              >
                                {index}
                              </Tooltip>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="chat-column chat-box-box">
                        <div className="chat-box-title-style">Feedback</div>
                        <div>
                          {chat?.Feedback ?? 0}{" "}
                          <ThumbsUp className="chat-box-icon-style" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ))}
      </div>
    );
  };

  return (
    <div className="custom-accordion-table">
      {data && data.length === 0 && (
        <div className="table-empty-state">No results found for the selected date range.</div>
      )}
      <table>
        <colgroup>
          {headers.map((header, index) => (
            <col key={`col-${index}`} style={{ width: header.width }} />
          ))}
          <col style={{ width: "auto" }} />
        </colgroup>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index} className="table-header" style={{ width: "3%" }}>
                {header.title}
              </th>
            ))}
            <th className="table-header"></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((row, rowIndex) => (
              <React.Fragment key={row[rowKey] || rowIndex}>
                <tr
                  className="table-row"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpanded(row);
                  }}
                >
                  {headers.map((header, colIndex) => {
                    const cellValue = row[header.title.replace(" ", "")];
                    const shouldTruncate = ["Organization", "District", "School", "UserPosition"].includes(header.title);
                    const displayValue = shouldTruncate ? truncate(cellValue) : cellValue;
                    
                    // Handle ODS column with line breaks
                    if (header.title === "ODS" && cellValue) {
                      const lines = cellValue.split(/\r\n|\n/);
                      return (
                        <td key={colIndex} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {lines.map((line, idx) => (
                            <div key={idx} style={idx === 0 ? { fontWeight: "bold" } : {}}>
                              {line}
                            </div>
                          ))}
                        </td>
                      );
                    }
                    
                    return <td key={colIndex}>{displayValue}</td>;
                  })}
                  <td>
                    <ChevronLeft
                      className={`chevron ${
                        expandedRowKeys.includes(row[rowKey])
                          ? "collapsed"
                          : "expanded"
                      }`}
                    />
                  </td>
                </tr>
                {expandedRowKeys.includes(row[rowKey]) && (
                  <tr className="expanded-row">
                    <td colSpan={headers.length + 1}>
                      {renderRowExpanded(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
        </tbody>
      </table>
    </div>
  );
}
